import { format } from "date-fns";
import axios from "http/customAxios";
import React, { useCallback, useState } from "react";
import { Alert, Button, Card } from "react-bootstrap";
import { useDropzone } from "react-dropzone";
import { defineMessages, FormattedMessage } from "react-intl";
import styled from "styled-components";
import { FullWidthLoader } from "../../../components/full-width-loader";
import { SectionHeading } from "../../../components/section-heading";
import { useApiClient } from "../../../lib/http";
import { createFileDownloadFromRawData } from "../../../lib/supportFunctions";
import { regenPropertyCache } from "../../common/api";
import { defaultFormMessages } from "../../shared/crud/form";
import { defaultListMessages } from "../../shared/crud/list";
import InventoryResultRenderer from "./inventoryResultRenderer";

export interface InventoryMultiUpdateProps {
  propertyTicker: string;
}

interface OperationError {
  message: string;
}

const messages = {
  ...defaultFormMessages,
  ...defaultListMessages,
  ...defineMessages({
    title: {
      id: "internalmenu.integrations.inventoryMultiUpdate",
      defaultMessage: "Update inventory",
    },
    description: {
      id: "inventoryMultiUpdate.description",
      defaultMessage: "Update inventory",
    },
    import: {
      id: "inventoryMultiUpdate.import",
      defaultMessage: "Import",
    },
    exportToExcel: {
      id: "inventoryMultiUpdate.exportToExcel",
      defaultMessage: "Export",
    },
    dropHere: {
      id: "app.imageManager.dropHere",
      defaultMessage: "Drop files here...",
    },
    warning: {
      id: "inventoryMultiUpdate.warning",
      defaultMessage: "warning",
    },
  }),
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin: 1rem;

  > * {
    margin: 1rem 0;
  }
`;
const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 1rem;

  > * {
    margin: 0 5px;
  }

  :last-child {
    margin-right: 5px;
  }
`;

const DropzoneContainer = styled.div`
  width: auto;
  height: 100px;
  border: 2px dashed #dae6e8;
  background-color: ${({ theme }) => theme.greyBackground};
  font-size: 21px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  margin-bottom: 25px;
  outline: none;
`;
const ResultContainer = styled.div`
  margin: 16px;
`;

function InventoryMultiUpdate({ propertyTicker }: InventoryMultiUpdateProps) {
  const cacheClient = useApiClient();

  const [file, setFile] = useState<File | undefined>(undefined);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<OperationError | null | undefined>(undefined);
  const [result, setResult] = useState<any>(undefined);
  const onDrop = useCallback(<T extends File>(acceptedFiles: T[]) => {
    if (!acceptedFiles || acceptedFiles.length === 0) {
      return;
    }
    setFile(acceptedFiles[0]);
  }, []);
  const { getRootProps, getInputProps } = useDropzone({ onDrop });
  const uploadURL = `/v6/api/v1/${propertyTicker}/inventory/multi-update`;
  const downloadURL = `/v6/api/v1/${propertyTicker}/inventory/export`;

  const handleImport = useCallback(async () => {
    if (!file) {
      return;
    }
    const formData = new FormData();
    formData.append("file", file);
    setLoading(true);
    setError(undefined);
    try {
      const response = await axios.post(uploadURL, formData, {
        headers: {
          "content-type": "multipart/form-data",
          "x-property": propertyTicker,
        },
      });
      setResult(response.data);
      // reset cache
      // FIXME: do this in the backend
      regenPropertyCache(cacheClient, propertyTicker).catch(() => setError({ message: "Error" }));
    } catch (err) {
      setError({ message: "Access denied" });
    }
    setLoading(false);
  }, [cacheClient, file, propertyTicker, uploadURL]);

  const handleDownload = useCallback(async () => {
    const response = await axios.get(downloadURL, {
      responseType: "blob",
      headers: {
        "x-property": propertyTicker,
      },
    });

    createFileDownloadFromRawData(
      response.data,
      `${propertyTicker}_${format(new Date(), "yyyyMMdd_HHmm")}`,
      "application/vnd.openxmlformats",
      "xlsx"
    );
  }, [downloadURL, propertyTicker]);

  if (isLoading) {
    return <FullWidthLoader />;
  }

  return (
    <>
      <SectionHeading>
        <FormattedMessage {...messages.title} />
      </SectionHeading>
      <Card className="ml-4 mr-4 mb-4 mt-4">
        <Container>
          <Alert variant="danger">
            <strong>
              <FormattedMessage {...messages.warning} />
            </strong>
          </Alert>
          <div>
            <FormattedMessage {...messages.description} />
          </div>
          <DropzoneContainer {...getRootProps()}>
            <input {...getInputProps()} />
            {file ? <div>{file.name}</div> : <FormattedMessage {...messages.dropHere} />}
          </DropzoneContainer>
        </Container>
        {error && (
          <Alert variant="danger">
            <strong>
              <FormattedMessage {...messages.saveError} />
            </strong>
          </Alert>
        )}
        <ButtonContainer>
          <Button variant="primary" type="button" onClick={handleImport}>
            <FormattedMessage {...messages.import} />
          </Button>
          <Button variant="primary" type="button" onClick={handleDownload}>
            <FormattedMessage {...messages.exportToExcel} />
          </Button>
        </ButtonContainer>
        {result && (
          <ResultContainer>
            <Alert variant="success">
              <strong>
                <FormattedMessage {...messages.saveSuccess} />
              </strong>
            </Alert>
            <InventoryResultRenderer inventoryResult={result} update />
          </ResultContainer>
        )}
      </Card>
    </>
  );
}

export default InventoryMultiUpdate;
